
import { Form, SelectOption, NumberField, TimePicker } from "../util";
import { defineComponent } from "vue";

import { ScheduleBody } from "@/domain/params";
import { professionalService, scheduleService } from "@/domain/services";
import { ScheduleModel } from "@/domain/models";
import { OptionSelect } from "@/presentation/models";
import { mapActions } from "vuex";
import hash from "object-hash";

type ComponentData = {
  schedule_id?: number;
  professional_id: number;
  isLoading: boolean;
  field_valid: { [field: string]: boolean };
  body: ScheduleBody;
  body_hash?: string;
  select_options: {
    specialties: OptionSelect[];
  };
  clocks: { start: boolean[][]; end: boolean[][] };
  professional_schedules: ScheduleModel[];
  disable_watch: { [name: string]: boolean };
};

export default defineComponent({
  name: "ScheduleForm",
  components: { Form, SelectOption, NumberField, TimePicker },
  data(): ComponentData {
    const schedule_id = Number(this.$route.params.schedule_id);

    const professional_id = Number(this.$route.query.professional_id);
    return {
      professional_id: professional_id,
      schedule_id,
      isLoading: false,
      field_valid: {
        week_day: !!schedule_id,
        max_visits: true,
        start_time: !!schedule_id,
        end_time: !!schedule_id,
      },
      body: {
        max_visits: 0,
        start_date: new Date(),
        end_date: undefined,
        start_time: undefined,
        end_time: undefined,
      },
      select_options: {
        specialties: [],
      },
      clocks: { start: [], end: [] },
      professional_schedules: [],
      disable_watch: { week_day: true, start_time: true },
    };
  },
  async created() {
    await this.populateSelects();
    await this.loadProfessionalSchedules();
    if (this.schedule_id) await this.loadSchedule(this.schedule_id);
  },
  computed: {
    isFormValid() {
      for (let field_name in this.field_valid)
        if (!this.field_valid[field_name]) return false;
      return true;
    },
  },
  methods: {
    save() {
      this.isLoading = true;
      const payload = { ...this.body };

      if (this.schedule_id && this.body_hash != hash(this.body))
        return scheduleService
          .update(this.schedule_id, payload as ScheduleBody)
          .then(() => {
            this.isLoading = false;
            this.loadSchedules(this.professional_id);
            this.$router.push("/schedules");
          })
          .catch(() => (this.isLoading = false));
      else if (!this.schedule_id)
        return scheduleService
          .create(payload as ScheduleBody)
          .then(() => {
            this.isLoading = false;
            this.loadSchedules(this.professional_id);
            this.$router.push("/schedules");
          })
          .catch(() => (this.isLoading = false));

      this.$router.push("/schedules");
    },
    closeModal(e: Event) {
      if (
        e.target == this.$refs.modal_mask ||
        e.target == this.$refs.close_modal
      )
        this.$router.push("/schedules");
    },
    updateValidation(field: string, valid: boolean) {
      this.field_valid[field] = valid;
    },
    async populateSelects() {
      await professionalService
        .getById(this.professional_id)
        .then((professional) => {
          this.select_options.specialties =
            professional.actuations?.map((a) => ({
              value: a.id,
              label: a.specialty_description + " - " + a.clinic_name,
            })) || [];
          this.body.acting_id = this.select_options.specialties[0]
            ?.value as number;
        });
    },
    async loadProfessionalSchedules() {
      await scheduleService
        .load({ professional_id: this.professional_id })
        .then((schedules) => {
          this.professional_schedules = schedules.filter(
            (s) => s.id !== this.schedule_id
          );
        })
        .catch(() => this.$router.push("/schedules"));
    },
    async loadSchedule(schedule_id: number) {
      this.isLoading = true;
      await scheduleService
        .getById(schedule_id)
        .then(async (schedule) => {
          this.body = {
            acting_id: schedule.acting_id,
            start_date: schedule.start_date,
            end_date: schedule.end_date,
            max_visits: schedule.max_visits,
            week_day: schedule.week_day,
          };

          this.body.start_time = schedule.start_time;
          this.body.end_time = schedule.end_time;
          this.updateClockStart(schedule.week_day);
          this.updateClockEnd(this.clocks.start, schedule.start_time);

          this.body_hash = hash(this.body);
          this.isLoading = false;
        })
        .catch(() => this.$router.push("/schedules"));
    },
    updateClockStart(weekday: number) {
      const schedules = this.professional_schedules.filter(
        (s) => s.week_day == weekday
      );
      let currrent_schedule = schedules.shift();
      let time_seconds;
      for (let i = 0; i < 24; i++) {
        this.clocks.start[i] = [];
        for (let j = 0; j < 60; j++) {
          time_seconds = i * 60 + j;
          if (currrent_schedule && time_seconds === currrent_schedule.end_time)
            currrent_schedule = schedules.shift();
          this.clocks.start[i][j] =
            !currrent_schedule || time_seconds < currrent_schedule.start_time;
        }
      }
    },
    updateClockEnd(startclock: boolean[][], start_seconds: number) {
      let time_seconds;
      for (let i = 0; i < 24; i++) {
        this.clocks.end[i] = [];
        for (let j = 0; j < 60; j++) {
          time_seconds = i * 60 + j;
          if (time_seconds <= start_seconds) this.clocks.end[i][j] = false;
          else this.clocks.end[i][j] = startclock[i][j];
        }
      }
    },
    ...mapActions({ loadSchedules: "schedules/load" }),
  },
  watch: {
    "body.week_day"(weekday: number) {
      if (this.disable_watch.week_day && this.schedule_id) {
        this.disable_watch.week_day = false;
        return;
      }
      this.updateClockStart(weekday);
      this.body.start_time = undefined;
    },
    "body.start_time"(time_seconds: number) {
      if (this.disable_watch.start_time && this.schedule_id) {
        this.disable_watch.start_time = false;
        return;
      }
      this.updateClockEnd(this.clocks.start, time_seconds);
      this.body.end_time = undefined;
    },
  },
});
